import React from 'react'
import { Row, Col, Form, Input, InputNumber, Select, Button, Icon, Tooltip } from 'antd'
import { internationalCodes, localCodes } from '@utils/index'
import { withFormContext, FormContextValue } from '../FormContext'
import AddressAutocomplete from '../AddressAutocomplete'
import {
    rtlInputNumber,
    textInput,
    selectInput,
    searchIcon,
    clientInfoWrap
} from '../../EditSubStoDataPopup/edit-sub-sto.scss'

const { Item: FormItem } = Form

// ── helpers ──────────────────────────────────────────────────
const digitsOnly = e => (e.target.value || '').replace(/[^\d]/g, '')

const getLocalCodes = (internationalCode?: string) => {
    if (!internationalCode) return localCodes['972']
    const codes = localCodes[internationalCode]
    return codes && codes.length ? codes : [{ value: '', label: '' }]
}

const globalCodes = internationalCodes.filter(c => c.show)

// ── field configs ────────────────────────────────────────────
const requiredRule = msg => ({ required: true, message: msg })

const clientFields = [
    {
        name: 'name',
        label: 'שם לקוח',
        placeholder: 'הזן שם לקוח',
        rules: [requiredRule('יש להזין שם לקוח')],
        disabled: true
    },
    {
        name: 'id',
        // prettier-ignore
        label: 'ת״ז/מס\' ח.פ. ברשות המיסים',
        placeholder: 'הזן מס',
        maxLength: 9,
        normalize: digitsOnly,
        // prettier-ignore
        rules: [requiredRule('יש להזין ת״ז/מס\' ח.פ.')],
        disabled: true
    },
    {
        name: 'address',
        label: 'כתובת לקוח',
        placeholder: 'הזן כתובת',
        rules: [],
        disabled: false,
        component: 'address'
    },
    {
        name: 'email',
        label: 'דוא״ל לקוח',
        placeholder: 'הזן דוא״ל',
        rules: [requiredRule('יש להזין דוא״ל'), { type: 'email', message: 'דוא״ל לא תקין' }],
        disabled: false
    },
    {
        name: 'client_id',
        label: '',
        placeholder: '',
        rules: [],
        disabled: true,
        type: 'hidden'
    },
    {
        name: 'client_full_name',
        label: '',
        placeholder: '',
        rules: [],
        disabled: true,
        type: 'hidden'
    },
    {
        name: 'user_sub_id',
        label: '',
        placeholder: '',
        rules: [],
        disabled: true,
        type: 'hidden'
    }
]

// ── sub-components ───────────────────────────────────────────
const ClientField = ({
    name,
    label,
    placeholder,
    maxLength,
    normalize,
    rules,
    getFieldDecorator,
    disabled,
    type,
    component
}: {
    name: any
    label: any
    placeholder: any
    maxLength?: any
    normalize?: any
    rules: any
    getFieldDecorator: any
    disabled: any
    type?: any
    component?: any
}) => {
    if (component === 'address') {
        return (
            <FormItem label={label} labelCol={{ style: { textAlign: 'right' } }}>
                {getFieldDecorator(name, {
                    rules,
                    initialValue: ''
                })(<AddressAutocomplete placeholder={placeholder} disabled={disabled} />)}
            </FormItem>
        )
    }

    return (
        <FormItem
            label={label}
            style={type ? { display: 'none' } : undefined}
            labelCol={{ style: { textAlign: 'right' } }}
        >
            {getFieldDecorator(name, {
                rules,
                initialValue: '',
                ...(normalize && { getValueFromEvent: normalize })
            })(
                <Input
                    className={textInput}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    disabled={disabled}
                    {...(type ? { type } : {})}
                />
            )}
        </FormItem>
    )
}

const PhoneField = ({ getFieldDecorator, internationalCode }) => {
    const codes = getLocalCodes(internationalCode)
    const isSingleCode = codes.length === 1

    return (
        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
            <FormItem labelCol={{ style: { textAlign: 'center' } }} label="קידומת בינלאומית" style={{ width: '40%' }}>
                {getFieldDecorator('phone_country_code', { initialValue: '972' })(
                    <Select className={selectInput}>
                        {globalCodes.map(c => (
                            <Select.Option className={selectInput} key={c.value} value={c.value}>
                                {c.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </FormItem>
            <FormItem labelCol={{ style: { textAlign: 'center' } }} label="קידומת" style={{ width: '15%' }}>
                {getFieldDecorator('phone_area_code', { initialValue: codes[0].value })(
                    <Select className={selectInput} disabled={isSingleCode}>
                        {codes.map(c => (
                            <Select.Option className={selectInput} key={c.value} value={c.value}>
                                {c.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </FormItem>
            <FormItem labelCol={{ style: { textAlign: 'center' } }} label="טלפון לקוח" style={{ width: '45%' }}>
                {getFieldDecorator('phone_number', {
                    rules: [requiredRule('יש להזין מספר טלפון')],
                    getValueFromEvent: digitsOnly,
                    initialValue: ''
                })(<Input className={textInput} placeholder="הזן טלפון" />)}
            </FormItem>
        </div>
    )
}

const ItemsHeader = () => (
    <Row gutter={8} style={{ fontWeight: 'bold', marginBottom: 10 }}>
        <Col span={1} />
        <Col span={4}>מחיר לחיוב</Col>
        <Col span={3}>כמות</Col>
        <Col span={5}>קוד</Col>
        <Col span={11}>שם</Col>
    </Row>
)

const ItemRow = ({ itemKey, index, isExtraCharge, getFieldDecorator, getFieldValue, removeRow, openSubModal }) => {
    const unitPrice = getFieldValue(`items[${index}].unit_price`)
    const code = getFieldValue(`items[${index}].code`)
    const name = getFieldValue(`items[${index}].name`)
    const isEmpty = !unitPrice && !code && !name

    return (
        <Row gutter={8} key={itemKey} style={{ marginBottom: 8 }}>
            <Col span={1}>
                {!isExtraCharge && (
                    <Icon
                        type="delete"
                        style={{ color: 'red', cursor: 'pointer', top: 'unset' }}
                        onClick={() => removeRow(itemKey)}
                    />
                )}
            </Col>
            <Col span={4}>
                <FormItem style={{ margin: 0 }}>
                    {getFieldDecorator(`items[${index}].unit_price`)(
                        <InputNumber
                            className={rtlInputNumber}
                            min={0}
                            step={0.1}
                            parser={v => v.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            disabled={isExtraCharge}
                        />
                    )}
                </FormItem>
            </Col>
            <Col span={3}>
                <FormItem style={{ margin: 0 }}>
                    {getFieldDecorator(`items[${index}].units_number`, { initialValue: 1 })(
                        <InputNumber className={rtlInputNumber} min={1} disabled />
                    )}
                </FormItem>
            </Col>
            <Col span={5}>
                <FormItem style={{ margin: 0 }}>
                    {getFieldDecorator(`items[${index}].code`)(
                        <Input className={textInput} placeholder={`G-000${index}`} disabled={isExtraCharge} />
                    )}
                </FormItem>
            </Col>
            <Col span={11} style={{ position: 'relative' }}>
                <FormItem style={{ margin: 0 }}>
                    {getFieldDecorator(`items[${index}].name`)(
                        <Input className={textInput} disabled={isExtraCharge} />
                    )}
                </FormItem>
                {!isExtraCharge && (
                    <div
                        style={{ display: isEmpty ? 'flex' : 'none' }}
                        className={searchIcon}
                        onClick={() => openSubModal(true, index)}
                    >
                        <Tooltip placement="top" title="חיפוש מוצר">
                            <Icon type="search" style={{ cursor: 'pointer', top: 'unset' }} />
                        </Tooltip>
                    </div>
                )}
            </Col>
            {/* hidden fields */}
            {getFieldDecorator(`items[${index}].type`, { initialValue: 'I' })(<Input type="hidden" />)}
            {getFieldDecorator(`items[${index}].price_type`, { initialValue: 'G' })(<Input type="hidden" />)}
            {getFieldDecorator(`items[${index}].internal_id`, { initialValue: null })(<Input type="hidden" />)}
            {getFieldDecorator(`items[${index}].vat_percent`)(<Input type="hidden" />)}
        </Row>
    )
}

// ── main component ───────────────────────────────────────────
const ClientInfo = ({
    internationalCode,
    items,
    existActiveOrder,
    addRow,
    removeRow,
    openSubModal,
    getFieldDecorator,
    getFieldValue,
    terminalType
}: {
    internationalCode: string
    items: any[]
    existActiveOrder: boolean
    addRow: () => void
    removeRow: (key: any) => void
    openSubModal: (flag: boolean, index?: number) => void
    terminalType: string
} & Pick<FormContextValue, 'getFieldDecorator' | 'getFieldValue'>) => {
    const safeItems = items || []
    const isBank = terminalType === 'mediwhobk'

    const hasRealItem = safeItems.some((key, index) => {
        const code = getFieldValue(`items[${index}].code`)
        return !['H-0010', 'H-0015'].includes(code)
    })

    const showAddButton = isBank ? !hasRealItem : safeItems.length === 0

    return (
        <>
            {clientFields.map(field => (
                <ClientField key={field.name} {...field} getFieldDecorator={getFieldDecorator} />
            ))}

            <PhoneField getFieldDecorator={getFieldDecorator} internationalCode={internationalCode} />

            <div>
                <div className={showAddButton || existActiveOrder ? clientInfoWrap : ''}>
                    {showAddButton && (
                        <Button type="link" onClick={addRow} style={{ padding: 'unset', marginRight: 'unset' }}>
                            הוספת פריט
                            <Icon type="plus-circle" style={{ color: '#52c41a' }} />
                        </Button>
                    )}
                    {existActiveOrder && (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>שימו לב! יש הו״ק פעילה למנוי</span>
                    )}
                </div>

                {safeItems.length > 0 && <ItemsHeader />}

                {safeItems.map((key, index) => {
                    const itemCode = getFieldValue(`items[${index}].code`)
                    const isExtraCharge = itemCode === 'H-0010' || itemCode === 'H-0015'
                    return (
                        <ItemRow
                            key={key}
                            itemKey={key}
                            index={index}
                            isExtraCharge={isExtraCharge}
                            getFieldDecorator={getFieldDecorator}
                            getFieldValue={getFieldValue}
                            removeRow={removeRow}
                            openSubModal={openSubModal}
                        />
                    )
                })}
            </div>
        </>
    )
}

export default withFormContext(ClientInfo)
